"use client";

import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw, ShoppingBag, CreditCard, Banknote, BarChart2 } from "lucide-react";
import DateRangePicker, { RangeWithPreset, defaultRange } from "@/components/DateRangePicker";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

const LOC_RESTAURANTE = "L5P2T0W4JGRC6";
const LOC_TAPAS       = "LAJM3467NBMRD";

type LocalFilter = "todo" | typeof LOC_RESTAURANTE | typeof LOC_TAPAS;

function fmt(n: number) { return n?.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €"; }
function fmtDec(n: number) { return n?.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"; }

function VariationBadge({ v }: { v: number | null }) {
  if (v === null) return null;
  const positive = v >= 0;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "3px",
      fontSize: "12px", fontWeight: 600, padding: "2px 8px", borderRadius: "20px",
      background: positive ? "#22c55e18" : "#ef444418",
      color: positive ? "#16a34a" : "#dc2626",
    }}>
      {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {positive ? "+" : ""}{v}%
    </span>
  );
}

function LocalSelector({ value, onChange }: { value: LocalFilter; onChange: (v: LocalFilter) => void }) {
  const opts: { key: LocalFilter; label: string }[] = [
    { key: "todo",          label: "Todo"        },
    { key: LOC_RESTAURANTE, label: "Restaurante" },
    { key: LOC_TAPAS,       label: "Tapas"       },
  ];
  return (
    <div style={{ display: "inline-flex", background: "var(--panel2, var(--bg-muted))", borderRadius: "10px", padding: "3px", gap: "2px" }}>
      {opts.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          style={{
            padding: "6px 14px", borderRadius: "8px", border: "none",
            fontSize: "13px", fontWeight: value === o.key ? 600 : 400,
            cursor: "pointer", transition: "all 150ms", fontFamily: "inherit",
            background: value === o.key ? "var(--acc, var(--accent))" : "transparent",
            color: value === o.key ? "#fff" : "var(--tx2, var(--text-muted))",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value, sub, variation, icon: Icon, color }: any) {
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={18} color={color} />
        </div>
        <VariationBadge v={variation} />
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "4px" }}>{label}</div>
      {sub && <div style={{ fontSize: "12px", color: "var(--text-light)", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

function MiniBarChart({ data, keyX, keyY, color = "var(--accent)" }: any) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d: any) => d[keyY] || 0), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "80px" }}>
      {data.map((d: any, i: number) => {
        const h = Math.max(2, ((d[keyY] || 0) / max) * 80);
        return (
          <div key={i} title={`${d[keyX]}: ${fmt(d[keyY])}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
            <div style={{ width: "100%", height: `${h}px`, background: color, borderRadius: "3px 3px 0 0", opacity: i === data.length - 1 ? 1 : 0.5, transition: "height 0.5s ease" }} />
          </div>
        );
      })}
    </div>
  );
}

function ProgresoMes({ proyeccion }: { proyeccion: any }) {
  if (!proyeccion) return null;
  const { ingresosHoy, objetivoMes, proyeccionMes, pctObjetivo, diaActual, diasEnMes } = proyeccion;
  const pctDias = Math.round((diaActual / diasEnMes) * 100);
  return (
    <div style={{ background: "var(--bg-dark)", borderRadius: "var(--radius)", padding: "24px", color: "var(--text-on-dark)" }}>
      <div style={{ fontSize: "13px", color: "var(--text-on-dark-muted)", marginBottom: "12px" }}>Progreso del mes</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "24px", fontWeight: 700 }}>{fmt(ingresosHoy)}</div>
          <div style={{ fontSize: "12px", color: "var(--text-on-dark-muted)" }}>Ventas acumuladas</div>
        </div>
        <div>
          <div style={{ fontSize: "24px", fontWeight: 700 }}>{fmt(proyeccionMes)}</div>
          <div style={{ fontSize: "12px", color: "var(--text-on-dark-muted)" }}>Proyección fin de mes</div>
        </div>
      </div>
      {objetivoMes > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-on-dark-muted)", marginBottom: "6px" }}>
            <span>Objetivo: {fmt(objetivoMes)}</span>
            <span>{pctObjetivo}%</span>
          </div>
          <div style={{ height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "6px" }}>
            <div style={{ height: "100%", width: `${Math.min(pctObjetivo || 0, 100)}%`, background: (pctObjetivo || 0) >= pctDias ? "#22c55e" : "var(--accent)", borderRadius: "6px", transition: "width 0.8s ease" }} />
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-on-dark-muted)", marginTop: "6px" }}>
            Día {diaActual} de {diasEnMes} — ritmo {(pctObjetivo || 0) >= pctDias ? "✅ adelantado" : "⚠️ por debajo"}
          </div>
        </>
      )}
    </div>
  );
}

export default function KpisPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filtro, setFiltro] = useState<LocalFilter>("todo");
  const [rango, setRango] = useState<RangeWithPreset>(defaultRange());

  const load = (r: RangeWithPreset = rango, attempt = 0) => {
    setLoading(true);
    const url = `${API}/kpis?desde=${r.desde}&hasta=${r.hasta}`;
    fetch(url).then(res => res.json()).then(d => {
      if (d.status === "syncing" && attempt < 6) {
        setTimeout(() => load(r, attempt + 1), 5000);
        return;
      }
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(rango); }, [rango]);

  const forceSync = async () => {
    setSyncing(true);
    await fetch(`${API}/kpis/sync`, { method: "POST" });
    setTimeout(() => { load(); setSyncing(false); }, 35000);
  };

  // ── Seleccionar datos según filtro ──
  const loc        = filtro !== "todo" ? data?.porLocal?.[filtro] : null;
  const totalVentas = filtro === "todo" ? (data?.totalVentas ?? 0) : (loc?.ventasMes ?? 0);
  const variacion   = filtro === "todo" ? (data?.variacion  ?? null) : (loc?.variacion ?? null);
  const historial   = filtro === "todo"
    ? data?.historialVentas
    : loc?.historialVentas;
  const desglose    = filtro === "todo" ? data?.desglosePago : loc?.desglosePago;
  const topProds    = filtro === "todo" ? data?.topProductos : loc?.topProductos;

  return (
    <div className="animate-fade-in" style={{ width: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: 700 }}>KPIs</h1>
          <p style={{ color: "var(--text-muted)", marginTop: "6px" }}>
            {data ? `Último sync: ${new Date(data.cachedAt).toLocaleTimeString("es-ES")}` : "Cargando datos..."}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <LocalSelector value={filtro} onChange={setFiltro} />
            <button onClick={forceSync} disabled={syncing} style={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "9px 16px", background: "var(--bg-muted)",
              border: "1px solid var(--border)", borderRadius: "10px",
              fontSize: "13px", cursor: "pointer", color: "var(--text-muted)",
              opacity: syncing ? 0.6 : 1,
            }}>
              <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
          </div>
          <DateRangePicker value={rango} onChange={(r) => { setRango(r); }} />
        </div>
      </div>

      {loading && !data ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px", gap: "12px", color: "var(--text-muted)" }}>
          <RefreshCw size={20} className="animate-spin" />
          <span>Descargando datos de Square...</span>
        </div>
      ) : !data || data.error ? (
        <div style={{ textAlign: "center", padding: "80px", color: "var(--text-light)" }}>
          <BarChart2 size={40} style={{ margin: "0 auto 16px", display: "block", opacity: 0.2 }} />
          <p>Error cargando datos. <button onClick={() => load()} style={{ color: "var(--accent)", background: "none", border: "none", cursor: "pointer" }}>Reintentar</button></p>
        </div>
      ) : (
        <>
          {/* KPIs principales */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
            <KpiCard label="Ventas del mes"      value={fmt(totalVentas)}          variation={variacion}       icon={TrendingUp}  color="#22c55e" />
            <KpiCard label="Ventas hoy"          value={fmt(data.ventasHoy)}       variation={null}            icon={ShoppingBag} color="#3b82f6" />
            <KpiCard label="Ticket medio"        value={fmtDec(data.ticketMedio)}  variation={data.varTicket}  icon={CreditCard}  color="#f59e0b" />
            <KpiCard label="Total transacciones" value={data.totalPagos}           sub="este mes"              icon={Banknote}    color="#a855f7" />
          </div>

          {/* Progreso + Desglose */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <ProgresoMes proyeccion={data.proyeccion} />
            <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "16px" }}>Desglose de pago</div>
              {[
                { label: "Tarjeta",  value: desglose?.tarjeta,  color: "#3b82f6" },
                { label: "Efectivo", value: desglose?.efectivo, color: "#22c55e" },
                { label: "Otros",    value: desglose?.otros,    color: "#9ca3af" },
              ].map(item => {
                const pct = totalVentas > 0 ? Math.round(((item.value ?? 0) / totalVentas) * 100) : 0;
                return (
                  <div key={item.label} style={{ marginBottom: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                      <span style={{ color: "var(--text-muted)" }}>{item.label}</span>
                      <span style={{ fontWeight: 600 }}>{fmt(item.value ?? 0)} <span style={{ color: "var(--text-light)", fontWeight: 400 }}>({pct}%)</span></span>
                    </div>
                    <div style={{ height: "5px", background: "var(--bg-muted)", borderRadius: "5px" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: item.color, borderRadius: "5px" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Historial diario + Top productos */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "16px" }}>Ventas diarias — mes actual</div>
              <MiniBarChart data={historial?.slice(-14)} keyX="fecha" keyY="ventas" color="var(--accent)" />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--text-light)", marginTop: "6px" }}>
                <span>{historial?.[0]?.fecha?.slice(5)}</span>
                <span>{historial?.[historial.length - 1]?.fecha?.slice(5)}</span>
              </div>
              {filtro === "todo" && data.mejorDia && (
                <div style={{ marginTop: "12px", padding: "10px 14px", background: "var(--bg-muted)", borderRadius: "10px", fontSize: "12px", color: "var(--text-muted)" }}>
                  🏆 Mejor día: <strong style={{ color: "var(--text)" }}>{data.mejorDia.fecha}</strong> — {fmt(data.mejorDia.ventas)}
                </div>
              )}
            </div>

            <div style={{ background: "var(--bg-card)", borderRadius: "var(--radius)", border: "1px solid var(--border)", padding: "24px", boxShadow: "var(--shadow)" }}>
              <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "16px" }}>Top productos</div>
              {!topProds?.length && <div style={{ color: "var(--text-light)", fontSize: "13px" }}>Sin datos</div>}
              {topProds?.map((p: any, i: number) => {
                const max = topProds[0]?.vendidos || 1;
                const pct = Math.round((p.vendidos / max) * 100);
                return (
                  <div key={i} style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "3px" }}>
                      <span style={{ color: "var(--text)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "70%" }}>{p.nombre}</span>
                      <span style={{ fontWeight: 600, color: "var(--text-muted)", flexShrink: 0 }}>{p.vendidos} uds</span>
                    </div>
                    <div style={{ height: "4px", background: "var(--bg-muted)", borderRadius: "4px" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: "var(--accent)", borderRadius: "4px" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
